/** Display helpers for property feature rules (Features tab + dispatch desk). */

import {
  CITATION_APPLY_BY,
  CITATION_BY,
  CITATION_PERIOD,
  SAFELISTING_BY,
  SAFELISTING_PERIOD,
} from "@/lib/constansts";

const PERMANENT_PARKING_YES_NO_KEYS = [
  "permanent_parking_pm_final_say",
  "permanent_parking_inspection_required",
];

export function isPermanentParkingYesNoRuleKey(key: string): boolean {
  return PERMANENT_PARKING_YES_NO_KEYS.includes(key);
}

/**
 * Maps select rule values to labels. API may store the option string or a 1-based index.
 * When `options` is missing, returns the raw string form of `value`.
 */
export function formatPropertyRuleSelectValue(
  value: unknown,
  options?: string[] | null,
): string {
  if (value === undefined || value === null || value === "") return "—";

  const asString = String(value).trim();
  if (!options?.length) return asString || "—";

  if (typeof value === "string" && options.includes(value)) return value;

  const numeric =
    typeof value === "number"
      ? value
      : /^-?\d+$/.test(asString)
        ? Number(asString)
        : Number.NaN;

  if (Number.isFinite(numeric)) {
    const oneBased = Math.trunc(numeric);
    if (oneBased >= 1 && oneBased <= options.length) return options[oneBased - 1]!;
    const zeroBased = Math.trunc(numeric);
    if (zeroBased >= 0 && zeroBased < options.length) return options[zeroBased]!;
  }

  return asString || "—";
}

export function normalizePropertyRuleOptions(raw: unknown): string[] | undefined {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item));
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return parsed.map((item) => String(item));
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function isPeriodByRule(key: string, name?: string): boolean {
  const k = key.toLowerCase();
  if (k.includes("period_by") || k.includes("periodby")) return true;
  const label = (name ?? "").toLowerCase();
  return /\bperiod\b/.test(label) && /\bby\b/.test(label);
}

function isApplyByRule(key: string): boolean {
  const k = key.toLowerCase();
  return k.includes("apply_by") || k.includes("apply_mode");
}

/**
 * Known select option lists when `/me` rules omit `options[]` (matches property config modal).
 */
export function resolvePropertyRuleSelectOptions(
  key: string,
  apiOptions?: string[] | null,
  context?: {name?: string; feature?: string},
): string[] | undefined {
  const fromApi = normalizePropertyRuleOptions(apiOptions);
  if (fromApi?.length) return fromApi;

  const k = key.toLowerCase();
  const feature = (context?.feature ?? "").toLowerCase();
  const isSafelistings =
    feature.includes("safelisting") || k.startsWith("safelisting") || k.startsWith("safelistings");
  const isCitations = feature.includes("citation") || k.startsWith("citation");

  if (isSafelistings) {
    if (isPeriodByRule(k, context?.name)) return [...SAFELISTING_PERIOD];
    if (k.includes("_by") || /\bby\b/i.test(context?.name ?? "")) return [...SAFELISTING_BY];
  }

  if (isCitations) {
    if (isApplyByRule(k)) return [...CITATION_APPLY_BY];
    if (isPeriodByRule(k, context?.name)) return [...CITATION_PERIOD];
    if (k.includes("_by") || /\bby\b/i.test(context?.name ?? "")) return [...CITATION_BY];
  }

  return undefined;
}
